import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import type { Marker as ClusterMapMarker, Renderer } from "@googlemaps/markerclusterer";
import type { ImageFolderPhoto } from "@shared/api";
import { toAbsoluteAssetUrl } from "@/lib/api-client";
import { buildGalleryMapMarkerIcon } from "@/lib/gallery-map-thumbnails";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

export type GeoTaggedGalleryPhoto = ImageFolderPhoto & {
  folder_name?: string;
  exif_latitude: number;
  exif_longitude: number;
};

const DEFAULT_CENTER = { lat: 48.67, lng: 19.5 };
/** Ne `min(70vh, 100%)`: szülő magasság nélkül a % gyakran 0 → üres térkép második megnyitásra is. */
const MAP_CONTAINER_STYLE: CSSProperties = {
  width: "100%",
  height: "70vh",
  minHeight: "70vh",
};

/** Max zoom: street-level without splitting nearby clusters into separate pins (tune with product). */
export const GALLERY_MAP_MAX_ZOOM = 17;

const GALLERY_PHOTO_KEY = "galleryPhoto";
const GALLERY_INDEX_KEY = "galleryIndex";

function galleryMapSortMs(p: GeoTaggedGalleryPhoto): number {
  return new Date(p.gallery_sort_at || p.created_at || 0).getTime();
}

/** Cluster thumbnail: most recently created photo in the group */
function pickRepresentativePhoto(markers: ClusterMapMarker[]): GeoTaggedGalleryPhoto | null {
  const items: GeoTaggedGalleryPhoto[] = [];
  for (const m of markers) {
    if (!(m instanceof google.maps.Marker)) continue;
    const p = m.get(GALLERY_PHOTO_KEY) as GeoTaggedGalleryPhoto | undefined;
    if (p) items.push(p);
  }
  if (!items.length) return null;
  items.sort((a, b) => galleryMapSortMs(b) - galleryMapSortMs(a));
  return items[0] ?? null;
}

/** All distinct photos in a cluster (newest first), for the viewer carousel */
export function photosFromClusterMarkers(markers: ClusterMapMarker[]): GeoTaggedGalleryPhoto[] {
  const seen = new Set<string>();
  const items: GeoTaggedGalleryPhoto[] = [];
  for (const m of markers) {
    if (!(m instanceof google.maps.Marker)) continue;
    const p = m.get(GALLERY_PHOTO_KEY) as GeoTaggedGalleryPhoto | undefined;
    if (!p?.id || seen.has(p.id)) continue;
    seen.add(p.id);
    items.push(p);
  }
  items.sort((a, b) => galleryMapSortMs(b) - galleryMapSortMs(a));
  return items;
}

function createGalleryClusterRenderer(): Renderer {
  return {
    render({ count, position, markers }) {
      const rep = pickRepresentativePhoto(markers);
      const thumbUrl = rep ? toAbsoluteAssetUrl(rep.thumbnail_url || rep.file_url) : null;

      const marker = new google.maps.Marker({
        position,
        zIndex: 1000 + count,
      });

      void buildGalleryMapMarkerIcon(thumbUrl, { clusterCount: count }).then((spec) => {
        marker.setIcon({
          url: spec.url,
          scaledSize: new google.maps.Size(spec.width, spec.height),
          anchor: new google.maps.Point(spec.anchorX, spec.anchorY),
        });
      });

      return marker;
    },
  };
}

/** API / JSON sometimes returns numeric columns as strings — normalize for map + filter */
function parseExifCoord(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function filterGeoPhotos(
  photos: Array<ImageFolderPhoto & { folder_name?: string }>
): GeoTaggedGalleryPhoto[] {
  const out: GeoTaggedGalleryPhoto[] = [];
  for (const p of photos) {
    const lat = parseExifCoord(p.exif_latitude);
    const lng = parseExifCoord(p.exif_longitude);
    if (lat === null || lng === null) continue;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;
    out.push({ ...p, exif_latitude: lat, exif_longitude: lng });
  }
  return out;
}

type GalleryMapViewProps = {
  photos: GeoTaggedGalleryPhoto[];
  className?: string;
  apiKey: string;
  onPhotoClick: (photo: GeoTaggedGalleryPhoto, index: number) => void;
  /** Multi-pin cluster: open viewer with these photos only (no map zoom). */
  onClusterPhotosClick: (photos: GeoTaggedGalleryPhoto[]) => void;
  labels: {
    loadError: string;
    apiMissing: string;
  };
};

const CLUSTER_RENDERER = createGalleryClusterRenderer();

export function GalleryMapView({
  photos,
  className,
  apiKey,
  onPhotoClick,
  onClusterPhotosClick,
  labels,
}: GalleryMapViewProps) {
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const onPhotoClickRef = useRef(onPhotoClick);
  onPhotoClickRef.current = onPhotoClick;
  const onClusterPhotosClickRef = useRef(onClusterPhotosClick);
  onClusterPhotosClickRef.current = onClusterPhotosClick;

  const { isLoaded, loadError } = useJsApiLoader({
    id: "tpt-gallery-map-script",
    googleMapsApiKey: apiKey,
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    let debug = false;
    try {
      debug =
        sessionStorage.getItem("galleryMapDebug") === "1" ||
        new URLSearchParams(window.location.search).get("galleryMapDebug") === "1";
    } catch {
      /* ignore */
    }
    if (!debug) return;
    const errMsg =
      loadError == null
        ? null
        : loadError instanceof Error
          ? loadError.message
          : String(loadError);
    const scripts = Array.from(document.querySelectorAll("script")).filter((s) => {
      const src = s.getAttribute("src") || "";
      return src.includes("maps.googleapis.com/maps/api/js");
    });
    const scriptSrcs = scripts.map((s) => s.getAttribute("src") || "");
    const scriptHasKeyParam = scriptSrcs.some((src) => /[?&]key=/.test(src));
    const scriptKeyLooksEmpty = scriptSrcs.some((src) => /[?&]key=(&|$)/.test(src));
    console.warn("[GALLERY-MAP-DEBUG] map loader", {
      apiKeyPresent: apiKey.trim().length > 0,
      apiKeyLength: apiKey.trim().length,
      isLoaded,
      loadError: errMsg,
      photoCount: photos.length,
      scriptCount: scripts.length,
      scriptHasKeyParam,
      scriptKeyLooksEmpty,
      scriptSrcs,
    });
  }, [apiKey, loadError, isLoaded, photos.length]);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  const onMapUnmount = useCallback(() => {
    setMapInstance(null);
  }, []);

  useEffect(() => {
    const map = mapInstance;
    if (!map || !isLoaded) return;

    const dispose = () => {
      clustererRef.current?.clearMarkers();
      clustererRef.current?.setMap(null);
      clustererRef.current = null;
      markersRef.current.forEach((m) => {
        google.maps.event.clearInstanceListeners(m);
        m.setMap(null);
      });
      markersRef.current = [];
    };

    dispose();
    if (!photos.length) return;

    const bounds = new google.maps.LatLngBounds();
    const markers: google.maps.Marker[] = photos.map((photo, index) => {
      const marker = new google.maps.Marker({
        position: { lat: photo.exif_latitude, lng: photo.exif_longitude },
      });
      marker.set(GALLERY_PHOTO_KEY, photo);
      marker.set(GALLERY_INDEX_KEY, index);

      const iconUrl = toAbsoluteAssetUrl(photo.thumbnail_url || photo.file_url);
      void buildGalleryMapMarkerIcon(iconUrl || null, {}).then((spec) => {
        marker.setIcon({
          url: spec.url,
          scaledSize: new google.maps.Size(spec.width, spec.height),
          anchor: new google.maps.Point(spec.anchorX, spec.anchorY),
        });
      });

      marker.addListener("click", () => onPhotoClickRef.current(photo, index));
      bounds.extend(marker.getPosition()!);
      return marker;
    });

    markersRef.current = markers;
    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      algorithm: new SuperClusterAlgorithm({
        maxZoom: GALLERY_MAP_MAX_ZOOM,
        radius: 100,
      }),
      renderer: CLUSTER_RENDERER,
      onClusterClick: (_event, cluster) => {
        const list = photosFromClusterMarkers(cluster.markers);
        if (list.length >= 2) {
          onClusterPhotosClickRef.current(list);
        }
      },
    });

    const clampZoom = () => {
      const z = map.getZoom();
      if (z != null && z > GALLERY_MAP_MAX_ZOOM) {
        map.setZoom(GALLERY_MAP_MAX_ZOOM);
      }
    };

    let fitBoundsIdleListener: google.maps.MapsEventListener | null = null;
    if (photos.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(Math.min(14, GALLERY_MAP_MAX_ZOOM));
    } else {
      map.fitBounds(bounds, 64);
      fitBoundsIdleListener = map.addListener("idle", () => {
        if (fitBoundsIdleListener) {
          google.maps.event.removeListener(fitBoundsIdleListener);
          fitBoundsIdleListener = null;
        }
        clampZoom();
      });
    }

    return () => {
      if (fitBoundsIdleListener) {
        google.maps.event.removeListener(fitBoundsIdleListener);
      }
      dispose();
    };
  }, [isLoaded, mapInstance, photos]);

  if (!apiKey.trim()) {
    return (
      <div className={cn("flex items-center justify-center p-8 text-muted-foreground text-center", className)}>
        {labels.apiMissing}
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={cn("flex items-center justify-center p-8 text-destructive text-center", className)}>
        {labels.loadError}
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-2 p-16 text-muted-foreground", className)}>
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className={cn("flex w-full flex-col", className)}>
      <GoogleMap
        mapContainerStyle={MAP_CONTAINER_STYLE}
        center={DEFAULT_CENTER}
        zoom={6}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        options={{
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          maxZoom: GALLERY_MAP_MAX_ZOOM,
        }}
      />
    </div>
  );
}
